import React, { useEffect, useState } from 'react';
import ButtonTag from '@components/framework/tags/ButtonTag.tsx';
import { SecuredConfig } from '@components/security/securedDecoration';
import SvgTagEdit from '@components/ringil3/icons/TagEdit.tsx';
import InputYesNo from '@components/ringil3/Input/InputSelect/InputYesNo.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { InputNumber } from '@components/framework/input/InputNumber';
import { Field } from '@components/framework/formfield/Field.tsx';
import { FieldLabel } from '@components/framework/formfield/FieldLabel.tsx';
import { isNullOrUndefined } from '@utils/utils.tsx';
import SvgParallelArrows from '@icons/parallel-arrows.svg?react';

export interface ConcurrentActionsValue {
  concurrentlyActiveEventsCount: number;
  excludePrivateRampsFromCapacity: boolean;
}

interface ComponentProps {
  secured?: SecuredConfig;
  concurrentlyActiveEventsCount: number;
  excludePrivateRampsFromCapacity: boolean;
  onChange: (value: ConcurrentActionsValue) => void;
  readOnly?: boolean;
  dataTestId?: string;
}

const LocationConcurrentActionsTag: React.FC<ComponentProps> = ({
  secured,
  concurrentlyActiveEventsCount,
  excludePrivateRampsFromCapacity,
  onChange,
  readOnly,
  dataTestId,
}) => {
  const { tr } = useTranslationLgs();
  const [internalValue, setInternalValue] = useState<ConcurrentActionsValue>({ concurrentlyActiveEventsCount, excludePrivateRampsFromCapacity });

  useEffect(() => setInternalValue({ concurrentlyActiveEventsCount, excludePrivateRampsFromCapacity }), [concurrentlyActiveEventsCount, excludePrivateRampsFromCapacity]);

  const displayValue = isNullOrUndefined(concurrentlyActiveEventsCount)
    ? null
    : excludePrivateRampsFromCapacity
      ? tr('LocalityDetail.concurrentActionsValueExclInternal', '{{cnt}} actions, excl. internal ramps', { cnt: concurrentlyActiveEventsCount })
      : tr('LocalityDetail.concurrentActionsValueInclInternal', '{{cnt}} actions, incl. internal ramps', { cnt: concurrentlyActiveEventsCount });

  const pane = (
    <div className="flex flex-col gap-3">
      <Field label={tr('LocalityDetail.concurrentActions', 'Number of concurrently running actions')}>
        <InputNumber
          value={internalValue.concurrentlyActiveEventsCount}
          onChange={e => setInternalValue(v => ({ ...v, concurrentlyActiveEventsCount: e.value }))}
          min={0}
          suffix={tr('LocalityDetail.actionsSuffix', ' actions')}
          data-testid={dataTestId ? `${dataTestId}Input` : undefined}
        />
      </Field>
      <div className="flex flex-col gap-1">
        <FieldLabel label={tr('LocalityDetail.excludeInternalRamps', "Don't count internal ramps toward the limit")} />
        <InputYesNo
          value={internalValue.excludePrivateRampsFromCapacity}
          onChange={v => setInternalValue(prev => ({ ...prev, excludePrivateRampsFromCapacity: v }))}
          dataTestId={dataTestId ? `${dataTestId}ExcludeInternal` : undefined}
        />
      </div>
    </div>
  );

  return (
    <ButtonTag
      secured={secured}
      label={tr('LocalityDetail.concurrentActions', 'Number of concurrently running actions')}
      icon={<SvgParallelArrows className="w-4 h-4" />}
      value={displayValue}
      actionIcon={<SvgTagEdit />}
      actionPane={pane}
      popupWidth={'380px'}
      onSave={onSuccess => {
        onChange(internalValue);
        onSuccess();
      }}
      readonly={readOnly}
      autoClose
      dataTestId={dataTestId}
      dataTestIdValue={dataTestId ? `${dataTestId}Value` : undefined}
    />
  );
};

export default LocationConcurrentActionsTag;
